import React, { useState } from 'react';
import { Table, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { primaryShades, variantTokens, greyScaleTokens } from '../../../theme/theme';
import CustomTooltip from '../Modals/CustomTooltip';

const { Text } = Typography;

// Costanti dei colori
const PRIMARY_300 = primaryShades.primary300;
const WHITE = variantTokens.white;
const GREY_200 = greyScaleTokens.grey200;
const LIGHT_GREY = '#f5f5f5';

// Constanti ombre
const SHADOW_SOFT = 'clamp(0rem, 0.1vw, 0rem) clamp(0.0625rem, 0.5vw, 0.125rem) clamp(0.3125rem, 2.5vw, 0.625rem) rgba(0,0,0,0.05)';

// Colori per il componente
const COLOR_TEXT = PRIMARY_300;
const COLOR_BACKGROUND = WHITE;
const COLOR_TEXT_ACTIVE = WHITE;
const COLOR_BACKGROUND_ACTIVE = PRIMARY_300;
const COLOR_BORDER = GREY_200;
const COLOR_BACKGROUND_HEADER = LIGHT_GREY;

// Componente per la riga di bottoni superiori
const CatalogSummary = ({ data, activeTab, onTabChange }) => {
    return (
        <div style={{
            display: 'flex',
            marginBottom: 'clamp(0.75rem, 3vw, 1.25rem)',
            width: '100%',
            borderRadius: 'clamp(0.25rem, 2vw, 0.5rem)',
            overflow: 'hidden',
            boxShadow: SHADOW_SOFT
        }}>
            {data.map((item, index) => {
                const isActive = activeTab === item.key;
                const isFirst = index === 0;
                const isLast = index === data.length - 1;

                return (
                    <div
                        key={item.key}
                        onClick={() => onTabChange(item.key)}
                        style={{
                            flex: 1,
                            padding: 'clamp(0.9375rem, 4vw, 1.875rem) clamp(0.625rem, 3vw, 1.25rem)',
                            cursor: 'pointer',
                            backgroundColor: isActive ? COLOR_BACKGROUND_ACTIVE : COLOR_BACKGROUND,
                            color: isActive ? COLOR_TEXT_ACTIVE : COLOR_TEXT,
                            borderRight: isLast ? 'none' : `clamp(0.0625rem, 0.2vw, 0.0625rem) solid ${COLOR_BORDER}`,
                            border: `clamp(0.0625rem, 0.2vw, 0.0625rem) solid ${COLOR_BORDER}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            textAlign: 'center',
                            borderTopLeftRadius: isFirst ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                            borderBottomLeftRadius: isFirst ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                            borderTopRightRadius: isLast ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                            borderBottomRightRadius: isLast ? 'clamp(0.25rem, 2vw, 0.5rem)' : '0',
                        }}
                    >
                        <div style={{
                            fontSize: 'clamp(1.5rem, 5vw, 2.25rem)',
                            fontWeight: 'bold',
                            color: isActive ? COLOR_TEXT_ACTIVE : COLOR_TEXT
                        }}>
                            {item.count}
                        </div>
                        <div style={{
                            fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                            fontWeight: 'bold',
                            marginTop: 'clamp(0.15625rem, 1vw, 0.3125rem)',
                            color: isActive ? COLOR_TEXT_ACTIVE : COLOR_TEXT
                        }}>
                            {item.label}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Componente per la riga di intestazione sotto alle tab
const TableHeader = ({ activeTab, total, timeSpent }) => {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: 'clamp(0.5rem, 2vw, 1rem)',
            backgroundColor: COLOR_BACKGROUND_HEADER,
            borderRadius: 'clamp(0.125rem, 1vw, 0.25rem)',
            marginBottom: 'clamp(0.5rem, 2vw, 1rem)',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 'clamp(0.5rem, 2vw, 1rem)'
        }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: 'clamp(0.875rem, 2.5vw, 1rem)'
            }}>
                <Text strong>{activeTab} - Time Focus</Text>
                <CustomTooltip title="Time spent viewing this catalog">
                    <InfoCircleOutlined style={{ marginLeft: 'clamp(0.25rem, 1vw, 0.5rem)' }} />
                </CustomTooltip>
            </div>
            <div style={{
                display: 'flex',
                gap: 'clamp(1rem, 4vw, 2rem)',
                flexWrap: 'wrap'
            }}>
                <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    <Text strong>Visualizations - Total {total}</Text>
                    <CustomTooltip title="Total number of visualizations">
                        <InfoCircleOutlined style={{ marginLeft: 'clamp(0.25rem, 1vw, 0.5rem)' }} />
                    </CustomTooltip>
                </div>
                <div style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    <Text strong>Time - Total Time Spent {timeSpent}</Text>
                    <CustomTooltip title="Total time spent on visualizations">
                        <InfoCircleOutlined style={{ marginLeft: 'clamp(0.25rem, 1vw, 0.5rem)' }} />
                    </CustomTooltip>
                </div>
            </div>
        </div>
    );
};

const TableBox = () => {
    // Definizione dei tab disponibili
    const tabsData = [
        { key: 'Lines', label: 'LINES', count: 27 },
        { key: 'Glasses', label: 'GLASSES', count: 383 },
        { key: 'Variants', label: 'VARIANTS', count: 1136 }
    ];

    // Dati di esempio per le varie tabelle
    const tablesData = {
        Lines: [
            { key: '1', name: 'SMITH OPTICS', visualizations: 3687, time: '23:40:32' },
            { key: '2', name: 'POLAROID', visualizations: 2916, time: '16:30:03' },
            { key: '3', name: 'CARRERA SUN', visualizations: 2564, time: '15:55:03' },
            { key: '4', name: 'CARRERA', visualizations: 928, time: '07:24:32' },
            { key: '5', name: 'POLAROID SPORT', visualizations: 395, time: '02:26:59' },
            { key: '6', name: 'CARRERA DUCATI', visualizations: 379, time: '01:54:28' },
            { key: '7', name: 'CARRERA SUN WOMAN', visualizations: 347, time: '01:57:57' },
            { key: '8', name: 'POLAROID KIDS', visualizations: 264, time: '01:19:12' },
            { key: '9', name: 'FLAG', visualizations: 215, time: '01:22:43' },
            { key: '10', name: 'CARRERA OPT WOMAN', visualizations: 175, time: '01:04:30' },
        ],
        Glasses: [
            { key: '1', name: 'SMITH OPTICS S3', visualizations: 1245, time: '08:30:22' },
            { key: '2', name: 'POLAROID X8402', visualizations: 1112, time: '07:12:43' },
            { key: '3', name: 'CARRERA 101S', visualizations: 978, time: '06:45:11' },
            { key: '4', name: 'SMITH RUCKUS', visualizations: 856, time: '05:52:28' },
            { key: '5', name: 'CARRERA 5050', visualizations: 735, time: '04:48:36' },
            { key: '6', name: 'POLAROID 4139', visualizations: 684, time: '04:22:15' },
            { key: '7', name: 'SMITH TRACE', visualizations: 567, time: '03:42:09' },
            { key: '8', name: 'CARRERA 1021', visualizations: 498, time: '03:12:30' },
            { key: '9', name: 'POLAROID 2112', visualizations: 412, time: '02:35:40' },
            { key: '10', name: 'SMITH WILDCAT', visualizations: 387, time: '02:18:17' },
        ],
        Variants: [
            { key: '1', name: 'SMITH OPTICS S3 TORTOISE', visualizations: 586, time: '04:12:36' },
            { key: '2', name: 'POLAROID X8402 BLACK', visualizations: 542, time: '03:45:22' },
            { key: '3', name: 'CARRERA 101S RED', visualizations: 498, time: '03:28:43' },
            { key: '4', name: 'CARRERA 101S BLACK', visualizations: 480, time: '03:16:28' },
            { key: '5', name: 'SMITH RUCKUS GREEN', visualizations: 455, time: '03:08:15' },
            { key: '6', name: 'SMITH RUCKUS WHITE', visualizations: 401, time: '02:44:23' },
            { key: '7', name: 'POLAROID 4139 BLUE', visualizations: 384, time: '02:36:18' },
            { key: '8', name: 'CARRERA 5050 MATT BLACK', visualizations: 351, time: '02:24:45' },
            { key: '9', name: 'SMITH TRACE SILVER', visualizations: 322, time: '02:08:33' },
            { key: '10', name: 'POLAROID 2112 HAVANA', visualizations: 298, time: '01:56:12' },
        ]
    };

    // Stato per il tab attivo
    const [activeTab, setActiveTab] = useState(tabsData[0].key);

    // Funzione per cambiare tab
    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
    };

    // Calcolo totali per tab attivo
    const calculateTotals = (data) => {
        const total = data.reduce((acc, item) => acc + item.visualizations, 0);

        // Calcolo del tempo totale
        let totalSeconds = 0;
        data.forEach(item => {
            const timeArr = item.time.split(':');
            const hours = parseInt(timeArr[0], 10);
            const minutes = parseInt(timeArr[1], 10);
            const seconds = parseInt(timeArr[2], 10);
            totalSeconds += hours * 3600 + minutes * 60 + seconds;
        });

        const formatTime = (seconds) => {
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = Math.floor(seconds % 60);
            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
        };

        return {
            visualizations: total,
            timeSpent: formatTime(totalSeconds)
        };
    };

    const totals = calculateTotals(tablesData[activeTab]);

    // Definizione delle colonne
    const columns = [
        {
            title: (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)'
                }}>
                    <span>{activeTab === 'Lines' ? 'Line' : (activeTab === 'Glasses' ? 'Glass' : 'Variant')}</span>
                    <CustomTooltip title="Details about the item">
                        <InfoCircleOutlined style={{ marginLeft: 'clamp(0.25rem, 1vw, 0.5rem)', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'name',
            key: 'name',
            sorter: (a, b) => a.name.localeCompare(b.name),
            width: '50%',
            align: 'left',
            render: (text) => <span style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>{text}</span>
        },
        {
            title: (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)'
                }}>
                    <span>Visualizations</span>
                    <CustomTooltip title="Number of times viewed">
                        <InfoCircleOutlined style={{ marginLeft: 'clamp(0.25rem, 1vw, 0.5rem)', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'visualizations',
            key: 'visualizations',
            sorter: (a, b) => a.visualizations - b.visualizations,
            defaultSortOrder: 'descend',
            width: '25%',
            align: 'right',
            render: (text) => <span style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>{text.toLocaleString()}</span>
        },
        {
            title: (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)'
                }}>
                    <span>Time</span>
                    <CustomTooltip title="Total time spent viewing">
                        <InfoCircleOutlined style={{ marginLeft: 'clamp(0.25rem, 1vw, 0.5rem)', cursor: 'pointer' }} />
                    </CustomTooltip>
                </div>
            ),
            dataIndex: 'time',
            key: 'time',
            sorter: (a, b) => {
                // Conversione del tempo in secondi per il confronto
                const convertToSeconds = (timeStr) => {
                    const parts = timeStr.split(':');
                    return parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10);
                };
                return convertToSeconds(a.time) - convertToSeconds(b.time);
            },
            width: '25%',
            align: 'right',
            render: (text) => <span style={{ fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>{text}</span>
        }
    ];

    return (
        <div style={{
            margin: 'clamp(0.5rem, 2vw, 1rem) 0',
            padding: 'clamp(0.5rem, 2vw, 1rem)',
            backgroundColor: WHITE,
            borderRadius: 'clamp(0.25rem, 2vw, 0.5rem)'
        }}>
            <CatalogSummary
                data={tabsData}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />

            <TableHeader
                activeTab={activeTab}
                total={totals.visualizations.toLocaleString()}
                timeSpent={totals.timeSpent}
            />

            <Table
                columns={columns}
                dataSource={tablesData[activeTab]}
                pagination={{
                    pageSize: 10,
                    size: 'small',
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    style: {
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        marginTop: 'clamp(0.5rem, 2vw, 1rem)'
                    }
                }}
                size="middle"
                style={{
                    width: '100%',
                    overflow: 'auto'
                }}
                scroll={{ x: 'max-content' }}
                rowClassName={() => 'custom-row'}
            />
        </div>
    );
};

export default TableBox;

export { CatalogSummary }; 